import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Translate, FloppyDisk, Check } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function LanguageConfigPage() {
  const [config, setConfig] = useState({ default_language: 'en', enabled_languages: ['en'], translations: {} });
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { Promise.all([fetchConfig(), fetchLanguages()]); }, []);

  const fetchConfig = async () => {
    try {
      const { data } = await axios.get(`${API}/api/seller/language-config`, { withCredentials: true });
      setConfig(c => ({ ...c, ...data }));
    } catch { /* defaults */ }
    finally { setLoading(false); }
  };

  const fetchLanguages = async () => {
    try {
      const { data } = await axios.get(`${API}/api/languages/supported`, { withCredentials: true });
      setLanguages(data.languages || []);
    } catch { /* ignore */ }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/seller/language-config`, config, { withCredentials: true });
      toast.success('Language config saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const toggleLang = (code) => {
    setConfig(c => ({
      ...c,
      enabled_languages: c.enabled_languages.includes(code)
        ? c.enabled_languages.filter(l => l !== code)
        : [...c.enabled_languages, code]
    }));
  };

  if (loading) return <DashboardLayout><div className="p-6 text-center text-[#9c9db7]">Loading...</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="p-6 max-w-3xl" data-testid="language-config-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0A0A0A]">Language Settings</h1>
            <p className="text-sm text-[#6b6b8a] mt-1">Configure supported languages for your storefront</p>
          </div>
          <Button data-testid="save-lang-btn" onClick={handleSave} disabled={saving} className="bg-[#7d64ff] hover:bg-[#6b4fef]">
            <FloppyDisk size={16} className="mr-2" /> {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-[#e5e5ea] p-5">
            <label className="text-sm font-semibold text-[#0A0A0A]">Default Language</label>
            <select data-testid="default-lang" value={config.default_language} onChange={e => setConfig(c => ({ ...c, default_language: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm mt-2">
              {languages.filter(l => config.enabled_languages.includes(l.code)).map(l => <option key={l.code} value={l.code}>{l.name} ({l.code})</option>)}
            </select>
          </div>

          <div className="bg-white rounded-xl border border-[#e5e5ea] p-5">
            <p className="text-sm font-semibold text-[#0A0A0A] mb-3">Enabled Languages ({config.enabled_languages.length})</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {languages.map(l => {
                const enabled = config.enabled_languages.includes(l.code);
                const isDefault = config.default_language === l.code;
                return (
                  <button key={l.code} onClick={() => { if (!isDefault) toggleLang(l.code); }} className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border text-left transition-all ${enabled ? 'border-[#7d64ff] bg-[#7d64ff]/5' : 'border-[#e5e5ea] hover:border-[#7d64ff]/30'} ${isDefault ? 'opacity-80 cursor-default' : 'cursor-pointer'}`} data-testid={`lang-${l.code}`}>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${enabled ? 'border-[#7d64ff] bg-[#7d64ff]' : 'border-gray-300'}`}>
                      {enabled && <Check size={10} weight="bold" className="text-white" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#0A0A0A]">{l.name}</p>
                      <p className="text-[10px] text-[#9c9db7] uppercase">{l.code}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="bg-[#f9f9fb] rounded-xl border border-[#e5e5ea] p-5 flex items-start gap-3">
            <Translate size={20} className="text-[#7d64ff] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-[#0A0A0A]">Translation Management</p>
              <p className="text-xs text-[#6b6b8a] mt-1">Translations for product titles, descriptions, and storefront content can be managed through the Product and Content editors once languages are enabled.</p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
